import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'

import { GET, httpOk } from 'helpers/api'
import { formatDate } from 'helpers/helpers'
import { checkTitle, checkHint, PROBLEM_TITLES } from './checkTitles'

// Диагностика точек глазами поддержки.
//
// Две задачи на одном экране. Сверху — список точек, у которых что-то сломано: интеграция
// без живого меню, курьерка без координат, пустая витрина. Это список для обзвона: клиент
// о таких поломках обычно не знает, потому что ошибок они не дают — заказы просто не идут.
// По клику на точку открывается тот же отчёт, что видит у себя клиент.
//
// Текст здесь по-русски и без t(): админка не переводится (правило проекта).

const STATUS_CLASSES = {
	ok: 'text-success',
	warning: 'text-warning',
	error: 'text-danger',
	skipped: 'text-muted',
}

// Кнопки над чек-листом: они же счётчики, они же фильтр по статусу
const STATUS_FILTERS = [
	{ status: 'error', name: 'Ошибок', activeClass: 'btn-danger' },
	{ status: 'warning', name: 'Предупреждений', activeClass: 'btn-warning' },
	{ status: 'ok', name: 'Настроено', activeClass: 'btn-success' },
	{ status: 'skipped', name: 'Не проверялось', activeClass: 'btn-secondary' },
]

// Названия постоянных модулей: у интеграций и каналов имя приходит с бэка (его задал клиент),
// а у этих оно фиксированное и живёт в локали кабинета, куда админке ходить не положено
const MODULE_TITLES = {
	fiscal: 'Фискализация',
	catalog: 'Витрина: бот, сайт, QR-меню',
	sms: 'SMS-уведомления',
	base: 'База точки',
	equipment: 'Оборудование',
}

function Index() {
	const [data, setData] = useState({ 'data': [], 'totals': {}, 'total': 0 })
	const [onlyProblems, setOnlyProblems] = useState(true)
	const [filterProblem, setFilterProblem] = useState(null)

	const [showModal, setShowModal] = useState(false)
	const [selectedPos, setSelectedPos] = useState(null)
	// Фильтр чек-листа внутри модалки: null — показать все проверки
	const [statusFilter, setStatusFilter] = useState(null)
	const [report, setReport] = useState(null)

	async function searchWithFilter(nextOnlyProblems = onlyProblems) {
		const response = await GET('/admin/setup-health/summary', { 'only_problems': nextOnlyProblems }, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	// Отчёт по конкретной точке — тот же, что открывает клиент в своём кабинете
	async function openPos(pos) {
		setSelectedPos(pos)
		setReport(null)
		setStatusFilter(null)
		setShowModal(true)

		const response = await GET(`/admin/pos/${pos.id}/setup-health`, {}, { loader: true })
		if (httpOk(response)) setReport(response.data)
	}

	function handleOnlyProblems(bool) {
		setOnlyProblems(bool)
		searchWithFilter(bool)
	}

	// Сколько проверок в таком статусе по всему отчёту — цифра на кнопке-фильтре
	function reportCount(report, status) {
		if (status === 'error') return report.summary.count_error
		if (status === 'warning') return report.summary.count_warning
		if (status === 'ok') return report.summary.count_ok

		return report.summary.count_skipped
	}

	const rows = filterProblem
		? data.data.filter((row) => row.problems.includes(filterProblem))
		: data.data

	useEffect(() => {
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{/* Переключатель раньше стоял в page-header: тот высотой 40px, и switch из него
				вылезал. Место ему рядом с фильтрами — он и есть фильтр */}
			<div className="page-header">
				<h4 className="d-flex align-items-center mb-0 h-100">Диагностика точек</h4>
			</div>

			{/* Плитки-фильтры: сколько точек по каждому виду поломки, клик оставляет только их.
				Видов со временем станет больше, поэтому сетка с переносом, а не пять в ряд */}
			<div className="row">
				<div className="col-xl-2 col-lg-3 col-md-4 col-6 mb-3">
					<div className={`card h-100 cursor-pointer ${filterProblem === null ? 'border-primary' : ''}`}
						onClick={() => setFilterProblem(null)}>
						<div className="card-body p-2 text-center">
							<div className="fw-bold fs-4">{data.total}</div>
							<div className="fs-6 text-muted">Все точки</div>
						</div>
					</div>
				</div>

				{Object.keys(PROBLEM_TITLES).map((code) => (
					<div className="col-xl-2 col-lg-3 col-md-4 col-6 mb-3" key={code}>
						<div className={`card h-100 cursor-pointer ${filterProblem === code ? 'border-primary' : ''}`}
							onClick={() => setFilterProblem(filterProblem === code ? null : code)}>
							<div className="card-body p-2 text-center">
								<div className={`fw-bold fs-4 ${data.totals[code] > 0 ? 'text-danger' : 'text-success'}`}>
									{data.totals[code] ?? 0}
								</div>
								<div className="fs-6">{PROBLEM_TITLES[code]}</div>
							</div>
						</div>
					</div>
				))}
			</div>

			<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
				<div className="form-check form-switch mb-0">
					<input type="checkbox" className="form-check-input" id="onlyProblems"
						checked={onlyProblems}
						onChange={(e) => handleOnlyProblems(e.target.checked)} />
					<label className="form-check-label" htmlFor="onlyProblems">Только с проблемами</label>
				</div>

				{/* Видно, какой фильтр сейчас включён, и как его снять: иначе пустая таблица
					выглядит как «точек нет», хотя это просто фильтр */}
				{filterProblem &&
					<button className="btn btn-sm btn-primary" onClick={() => setFilterProblem(null)}>
						{PROBLEM_TITLES[filterProblem]}
						<i className="uil uil-times ms-1"></i>
					</button>
				}

				<span className="ms-auto text-muted fs-6">Показано точек: {rows.length}</span>
			</div>

			<div className="card">
				<div className="card-body">
					<table className="table table-bordered table-hover mb-0">
						<thead>
							<tr>
								<th className="text-center" style={{ 'width': '33px' }}>#</th>
								<th>Точка</th>
								<th>Логин</th>
								<th>Что не так</th>
								<th style={{ 'width': '120px' }}></th>
							</tr>
						</thead>
						<tbody>
							{rows.map((row, index) => (
								<tr key={row.id}>
									<td className="text-center">{index + 1}</td>
									<td>{row.name}</td>
									<td>{row.login}</td>
									<td>
										{row.problems.map((code) => (
											<span className="badge bg-danger me-1" key={code}>{PROBLEM_TITLES[code]}</span>
										))}
										{row.problems.length === 0 &&
											<span className="text-success">Всё настроено</span>
										}
									</td>
									<td className="text-end">
										<button className="btn btn-sm btn-outline-primary" onClick={() => openPos(row)}>
											Открыть отчёт
										</button>
									</td>
								</tr>
							))}

							{rows.length === 0 &&
								<tr>
									<td colSpan="5" className="text-center text-muted">Точек с проблемами нет</td>
								</tr>
							}
						</tbody>
					</table>
				</div>
			</div>

			{/* MODAL — полный отчёт по точке. Отчёт длинный (у крупного бренда под сотню
				проверок), поэтому прокручивается внутри окна, а не вытягивает страницу.
				`size="xl"` — родной bootstrap-размер: класса `update-modal-width`, который
				в проекте копируют из формы в форму, в стилях нет вовсе, и он ничего не делает */}
			<Modal show={showModal} animation={false} centered scrollable size="xl"
				onHide={() => setShowModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{selectedPos?.name}
						<span className="ms-2 text-muted fs-6">{selectedPos?.login}</span>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ 'height': '500px' }}>
					{!report &&
						<div className="text-center text-muted">Загружаем…</div>
					}

					{report &&
						<>
							{/* Сводка — она же фильтр: у крупного бренда под сотню проверок,
								и дежурному нужны восемь красных, а не весь список */}
							<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
								{STATUS_FILTERS.map((item) => (
									<button key={item.status}
										className={`btn btn-sm ${statusFilter === item.status ? item.activeClass : 'btn-outline-secondary'}`}
										onClick={() => setStatusFilter(statusFilter === item.status ? null : item.status)}>
										{item.name}: {reportCount(report, item.status)}
									</button>
								))}

								{/* Состояние драйвера и принтеров приходит из последнего ответа кассы */}
								<span className="ms-auto text-muted fs-6">
									{report.probe
										? `Касса проверена: ${formatDate(report.probe.checked_at, 'dd.MM.yyyy HH:mm')}`
										: 'Кассу ещё не проверяли'}
								</span>
							</div>

							{report.modules.map((module) => {
								const checks = statusFilter
									? module.checks.filter((check) => check.status === statusFilter)
									: module.checks

								// Модуль, в котором после фильтра ничего не осталось, не показываем:
								// иначе экран забит пустыми заголовками
								if (checks.length === 0) return null

								return (
									<div className="card mb-2" key={module.key}>
										<div className="card-body py-2">
											<h6 className="mb-2">
												<span className={`me-2 ${STATUS_CLASSES[module.status]}`}>●</span>
												{module.title || MODULE_TITLES[module.key] || module.key}
												<span className="ms-2 text-muted fs-6">
													{module.count_ok} из {module.count_total - module.count_skipped}
												</span>
											</h6>

											{checks.map((check) => (
												<div className="d-flex justify-content-between py-1 border-bottom" key={check.code}>
													<div>
														<div className={check.status === 'ok' ? '' : STATUS_CLASSES[check.status]}>
															{checkTitle(check)}
														</div>

														{/* У проблемной проверки показываем, что именно не так:
															код вида products_fiscal_code дежурному ничего не говорит */}
														{check.status !== 'ok' &&
															<div className={`fs-6 ${STATUS_CLASSES[check.status]}`}>
																{checkHint(check)}
															</div>
														}
													</div>

													<div className="text-muted fs-6 text-end ms-2" style={{ 'maxWidth': '45%' }}>
														{check.description || (check.status === 'ok' ? 'Настроено' : '')}
													</div>
												</div>
											))}
										</div>
									</div>
								)
							})}
						</>
					}
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Index
